import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plus, Trash2, Check, X, GitMerge, Download } from 'lucide-react';
import api from '../api';
import ErrorAlert from '../components/ErrorAlert';

const TAXONOMY_FAMILIES = [
  { id: 'use_case_tag', label: 'Card Tags' },
  { id: 'category', label: 'Card Categories' },
  { id: 'sector', label: 'Place Sectors' },
  { id: 'category_type', label: 'Place Categories' },
  { id: 'utility_kind', label: 'Utility Kinds' },
  { id: 'booking_logic', label: 'Booking Logic' },
  { id: 'weather_logic', label: 'Weather Logic' },
  { id: 'parking_access', label: 'Arrival & Parking' },
  { id: 'budget', label: 'Budget' },
  { id: 'seasonality', label: 'Seasonality' },
  { id: 'practical_use', label: 'Practical Uses' },
  { id: 'fact_warning_label', label: 'Fact Warning Labels' },
];

function familyLabel(family) {
  return TAXONOMY_FAMILIES.find(f => f.id === family)?.label || family.replace(/_/g, ' ');
}

function statusStyle(status) {
  if (status === 'approved') return 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100';
  if (status === 'pending') return 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100';
  return 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200';
}

function facetLabel(facet) {
  if (!facet || facet === 'ungrouped') return 'Ungrouped';
  return facet.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function facetAccent(facet) {
  const accents = {
    audience: 'border-l-blue-500 bg-blue-50 text-blue-900',
    occasion: 'border-l-rose-500 bg-rose-50 text-rose-900',
    access: 'border-l-cyan-500 bg-cyan-50 text-cyan-900',
    activity: 'border-l-emerald-500 bg-emerald-50 text-emerald-900',
    food_drink: 'border-l-orange-500 bg-orange-50 text-orange-900',
    culture: 'border-l-violet-500 bg-violet-50 text-violet-900',
    shopping: 'border-l-pink-500 bg-pink-50 text-pink-900',
    practical: 'border-l-slate-500 bg-slate-50 text-slate-900',
    seasonality: 'border-l-amber-500 bg-amber-50 text-amber-900',
    booking: 'border-l-indigo-500 bg-indigo-50 text-indigo-900',
    weather: 'border-l-sky-500 bg-sky-50 text-sky-900',
    budget: 'border-l-lime-500 bg-lime-50 text-lime-900',
  };
  return accents[facet] || 'border-l-gray-400 bg-gray-50 text-gray-800';
}

function groupCounts(terms) {
  return terms.reduce((counts, term) => {
    counts.total += 1;
    counts[term.status] = (counts[term.status] || 0) + 1;
    return counts;
  }, { total: 0, approved: 0, pending: 0, retired: 0 });
}

function sortTerms(terms) {
  return [...terms].sort((a, b) => {
    const facet = String(a.facet || '').localeCompare(String(b.facet || ''));
    if (facet !== 0) return facet;
    return String(a.label || a.value).localeCompare(String(b.label || b.value));
  });
}

function sortFacets(facets) {
  return [...facets].sort((a, b) => {
    const status = String(a.status || '').localeCompare(String(b.status || ''));
    if (status !== 0) return status;
    return String(a.label || a.value).localeCompare(String(b.label || b.value));
  });
}

const TERM_EXPORT_FIELDS = [
  'id',
  'family',
  'family_label',
  'value',
  'label',
  'facet',
  'status',
  'usage_count',
  'is_seed',
  'created_by',
  'sort_order',
  'created_from_card_id',
  'merged_into_id',
  'approved_by',
  'approved_at',
  'created_at',
];

function csvValue(value) {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows, fields) {
  const body = [
    fields.map(csvValue).join(','),
    ...rows.map(row => fields.map(field => csvValue(row[field])).join(',')),
  ].join('\n');
  const url = URL.createObjectURL(new Blob([`${body}\n`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function TermRow({ term, terms, facetOptions, onRename, onFacet, onStatus, onDelete, onMerge }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(term.label);
  const [mergeTarget, setMergeTarget] = useState('');
  const [saving, setSaving] = useState(false);

  const sameFamilyTargets = terms.filter(t => t.id !== term.id && t.family === term.family && t.status === 'approved');
  const isRetired = term.status === 'retired';
  const currentFacetKnown = !term.facet || facetOptions.some(facet => facet.value === term.facet);
  const visibleFacetOptions = currentFacetKnown
    ? facetOptions
    : [{ value: term.facet, label: `${facetLabel(term.facet)} (not approved)` }, ...facetOptions];

  const save = async () => {
    setSaving(true);
    try {
      await onRename(term.id, label);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={`hover:bg-gray-50 ${isRetired ? 'opacity-45' : ''}`}>
      <td className="px-4 py-3 font-mono text-xs text-gray-500">{term.value}</td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={label}
              onChange={e => setLabel(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
              className="px-2 py-1 border border-blue-400 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button onClick={save} disabled={saving} className="p-1 text-green-600 hover:text-green-700"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditing(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <span
            className="text-sm text-gray-800 cursor-pointer hover:text-blue-600"
            onClick={() => setEditing(true)}
            title="Click to rename"
          >
            {term.label}
          </span>
        )}
        {term.merged_into_id && <p className="text-xs text-gray-400 mt-1">Merged into #{term.merged_into_id}</p>}
      </td>
      <td className="px-4 py-3">
        <select
          value={term.facet || ''}
          onChange={e => onFacet(term.id, e.target.value)}
          className="px-2 py-1 border border-gray-300 rounded text-xs bg-white"
        >
          {visibleFacetOptions.map(facet => (
            <option key={facet.value || 'none'} value={facet.value}>{facet.label}</option>
          ))}
        </select>
      </td>
      <td className="px-4 py-3">
        {term.status === 'pending' ? (
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => onStatus(term.id, 'approved')}
              className="rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100"
            >
              Approve
            </button>
            <button
              onClick={() => onStatus(term.id, 'retired')}
              className="rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
            >
              Retire
            </button>
          </div>
        ) : (
          <button
            onClick={() => onStatus(term.id, term.status === 'approved' ? 'retired' : 'approved')}
            className={`text-xs px-2.5 py-1 rounded-full font-medium border transition-colors ${statusStyle(term.status)}`}
          >
            {term.status}
          </button>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {Number.isFinite(term.usage_count) ? term.usage_count : '-'}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-1">
          <select
            value={mergeTarget}
            onChange={e => setMergeTarget(e.target.value)}
            disabled={isRetired}
            className="min-w-0 max-w-44 px-2 py-1 border border-gray-300 rounded text-xs bg-white disabled:opacity-50"
          >
            <option value="">Merge into…</option>
            {sameFamilyTargets.map(target => (
              <option key={target.id} value={target.id}>{target.label}</option>
            ))}
          </select>
          <button
            onClick={() => mergeTarget && onMerge(term.id, Number(mergeTarget))}
            disabled={!mergeTarget || isRetired}
            title="Merge duplicate into selected term"
            className="p-1.5 text-gray-400 hover:text-blue-600 disabled:opacity-30"
          >
            <GitMerge className="w-4 h-4" />
          </button>
        </div>
      </td>
      <td className="px-4 py-3 text-right">
        <button
          onClick={() => { if (window.confirm(`Retire or delete "${term.label}"? Pending unused terms are deleted; used terms are retired.`)) onDelete(term.id); }}
          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
          title="Retire/delete"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
}

export default function VocabularyTermsView({ family = null, title = 'Taxonomy', embedded = false }) {
  const [activeFamily, setActiveFamily] = useState(family || 'use_case_tag');
  const [terms, setTerms] = useState([]);
  const [facets, setFacets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newFacet, setNewFacet] = useState('');
  const [newFacetLabel, setNewFacetLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [addingFacet, setAddingFacet] = useState(false);
  const [exportingTerms, setExportingTerms] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const fixedFamily = Boolean(family);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [termData, facetData] = await Promise.all([
        api.getVocabularyTerms(activeFamily, null, { includeUsage: true }),
        api.getVocabularyFacets(activeFamily, null, { includeUsage: true }),
      ]);
      setTerms(sortTerms(termData));
      setFacets(sortFacets(facetData));
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [activeFamily]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      const term = await api.createVocabularyTerm(activeFamily, newLabel.trim(), { facet: newFacet || null });
      setTerms(ts => sortTerms([...ts.filter(t => t.id !== term.id), term]));
      setNewLabel('');
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const handleAddFacet = async (e) => {
    e.preventDefault();
    if (!newFacetLabel.trim()) return;
    setAddingFacet(true);
    try {
      const facet = await api.createVocabularyFacet(activeFamily, newFacetLabel.trim(), {
        raw_json: { source: 'taxonomy_tab' },
      });
      setFacets(fs => sortFacets([...fs.filter(f => f.id !== facet.id), facet]));
      setNewFacetLabel('');
    } catch (e) {
      setError(e.message);
    } finally {
      setAddingFacet(false);
    }
  };

  const handleRename = async (id, label) => {
    const updated = await api.updateVocabularyTerm(id, { label });
    setTerms(ts => sortTerms(ts.map(t => t.id === id ? updated : t)));
  };

  const handleFacet = async (id, facet) => {
    const updated = await api.updateVocabularyTerm(id, { facet: facet || null });
    setTerms(ts => sortTerms(ts.map(t => t.id === id ? updated : t)));
  };

  const handleFacetStatus = async (id, status) => {
    const updated = await api.updateVocabularyFacet(id, { status });
    setFacets(fs => sortFacets(fs.map(f => f.id === id ? updated : f)));
  };

  const handleStatus = async (id, status) => {
    const updated = await api.updateVocabularyTerm(id, { status });
    setTerms(ts => sortTerms(ts.map(t => t.id === id ? updated : t)));
  };

  const handleDelete = async (id) => {
    await api.deleteVocabularyTerm(id);
    await load();
  };

  const handleMerge = async (id, targetId) => {
    await api.mergeVocabularyTerm(id, targetId, 'admin taxonomy merge');
    await load();
  };

  const exportAllTerms = async () => {
    setExportingTerms(true);
    setError('');
    try {
      const loaded = await Promise.all(
        TAXONOMY_FAMILIES.map(item => api.getVocabularyTerms(item.id, null, { includeUsage: true }))
      );
      const exportRows = loaded
        .flat()
        .map(term => ({
          ...term,
          family_label: familyLabel(term.family),
          facet: term.facet || '',
          usage_count: Number.isFinite(term.usage_count) ? term.usage_count : '',
          is_seed: term.is_seed ? 'true' : 'false',
        }))
        .sort((a, b) => (
          String(a.family).localeCompare(String(b.family))
          || String(a.facet || '').localeCompare(String(b.facet || ''))
          || String(a.value || '').localeCompare(String(b.value || ''))
        ));
      downloadCsv(
        `taxonomy_terms_all_${new Date().toISOString().slice(0, 10)}.csv`,
        exportRows,
        TERM_EXPORT_FIELDS,
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setExportingTerms(false);
    }
  };

  const retiredCount = terms.filter(t => t.status === 'retired').length;
  const pendingCount = terms.filter(t => t.status === 'pending').length;
  const pendingFacetCount = facets.filter(f => f.status === 'pending').length;
  const approvedFacetOptions = facets
    .filter(f => f.status === 'approved')
    .map(f => ({ value: f.value, label: f.label || facetLabel(f.value) }));
  const facetOptions = [{ value: '', label: 'Ungrouped' }, ...approvedFacetOptions];
  const visible = showRetired ? terms : terms.filter(t => t.status !== 'retired');
  const grouped = visible.reduce((acc, term) => {
    const group = term.facet || 'ungrouped';
    if (!acc[group]) acc[group] = [];
    acc[group].push(term);
    return acc;
  }, {});
  const groupNames = Object.keys(grouped).sort((a, b) => (a === 'ungrouped' ? 1 : b === 'ungrouped' ? -1 : a.localeCompare(b)));

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && <h1 className="text-xl font-semibold text-gray-900">{title}</h1>}
          <p className="text-sm text-gray-500">
            {familyLabel(activeFamily)} · {terms.filter(t => t.status === 'approved').length} approved
            {pendingCount > 0 && `, ${pendingCount} pending`}
            {retiredCount > 0 && `, ${retiredCount} retired`}
            {pendingFacetCount > 0 && ` · ${pendingFacetCount} pending facet${pendingFacetCount === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {retiredCount > 0 && (
            <button
              onClick={() => setShowRetired(s => !s)}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              {showRetired ? 'hide retired' : `show ${retiredCount} retired`}
            </button>
          )}
          <button onClick={load} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={exportAllTerms}
            disabled={exportingTerms}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {exportingTerms ? 'Preparing...' : 'Download all terms CSV'}
          </button>
        </div>
      </div>

      {!fixedFamily && (
        <div className="flex flex-wrap gap-2">
          {TAXONOMY_FAMILIES.map(item => (
            <button
              key={item.id}
              type="button"
              onClick={() => setActiveFamily(item.id)}
              className={`px-3 py-1.5 rounded-md text-sm border ${
                activeFamily === item.id
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-gray-600 border-gray-300 hover:border-slate-600'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {error && <ErrorAlert error={error} onClose={() => setError('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">Facets for {familyLabel(activeFamily)}</p>
            <p className="mt-0.5 text-xs text-gray-500">Facet names are scoped to this family. New facets start pending.</p>
          </div>
          <form onSubmit={handleAddFacet} className="flex flex-wrap gap-2">
            <input
              type="text"
              placeholder="Propose new facet..."
              value={newFacetLabel}
              onChange={e => setNewFacetLabel(e.target.value)}
              className="h-9 w-52 rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
            <button
              type="submit"
              disabled={addingFacet || !newFacetLabel.trim()}
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-gray-900 px-3 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Propose facet
            </button>
          </form>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {facets.length === 0 && <span className="text-xs text-gray-400">No governed facets yet.</span>}
          {facets.map(facet => (
            <span key={facet.id} className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyle(facet.status)}`}>
              {facet.label || facetLabel(facet.value)}
              <span className="text-[11px] opacity-70">{Number(facet.usage_count || 0).toLocaleString()} terms</span>
              {facet.status === 'pending' && (
                <>
                  <button type="button" onClick={() => handleFacetStatus(facet.id, 'approved')} className="text-green-700 underline">approve</button>
                  <button type="button" onClick={() => handleFacetStatus(facet.id, 'retired')} className="text-gray-600 underline">retire</button>
                </>
              )}
              {facet.status === 'approved' && (
                <button type="button" onClick={() => handleFacetStatus(facet.id, 'retired')} className="text-gray-600 underline">retire</button>
              )}
            </span>
          ))}
        </div>
      </div>

      <form onSubmit={handleAdd} className="flex flex-wrap gap-2">
        <input
          type="text"
          placeholder="New label…"
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
        />
        <select
          value={newFacet}
          onChange={e => setNewFacet(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {facetOptions.map(facet => (
            <option key={facet.value || 'none'} value={facet.value}>{facet.label}</option>
          ))}
        </select>
        <button
          type="submit"
          disabled={adding || !newLabel.trim()}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add Pending
        </button>
      </form>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-medium w-56">Key</th>
              <th className="px-4 py-3 text-left font-medium">Label <span className="normal-case font-normal text-gray-400">(click to rename)</span></th>
              <th className="px-4 py-3 text-left font-medium w-40">Facet</th>
              <th className="px-4 py-3 text-left font-medium w-36">Status</th>
              <th className="px-4 py-3 text-left font-medium w-20">Used</th>
              <th className="px-4 py-3 text-left font-medium w-60">Merge</th>
              <th className="px-4 py-3 w-12" />
            </tr>
          </thead>
          {groupNames.map(group => {
            const counts = groupCounts(grouped[group]);
            return (
            <tbody key={group} className="divide-y divide-gray-100">
              <tr>
                <td colSpan="7" className={`border-l-4 px-4 py-3 ${facetAccent(group)}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold tracking-wide">{facetLabel(group)}</p>
                      <p className="mt-0.5 text-xs opacity-70">
                        {counts.total} term{counts.total === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-xs">
                      <span className="rounded-full border border-white/70 bg-white/70 px-2 py-0.5 font-medium">
                        {counts.approved} approved
                      </span>
                      {counts.pending > 0 && (
                        <span className="rounded-full border border-yellow-200 bg-yellow-100 px-2 py-0.5 font-medium text-yellow-800">
                          {counts.pending} pending
                        </span>
                      )}
                      {counts.retired > 0 && (
                        <span className="rounded-full border border-gray-200 bg-white/70 px-2 py-0.5 font-medium text-gray-600">
                          {counts.retired} retired
                        </span>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
              {grouped[group].map(term => (
                <TermRow
                  key={term.id}
                  term={term}
                  terms={terms}
                  facetOptions={facetOptions}
                  onRename={handleRename}
                  onFacet={handleFacet}
                  onStatus={handleStatus}
                  onDelete={handleDelete}
                  onMerge={handleMerge}
                />
              ))}
            </tbody>
            );
          })}
        </table>
        {visible.length === 0 && (
          <p className="text-center py-10 text-gray-400 text-sm">No {familyLabel(activeFamily).toLowerCase()}</p>
        )}
      </div>
    </div>
  );
}
